import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Briefcase, Plus, X, CheckCircle2, KeyRound, ExternalLink,
  AlertTriangle, PowerOff, Power, Eye, Users, Gift, Copy, Check, Image as ImageIcon,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const FREIGHT_TYPES = [
  { code: "dry_van", label: "Dry Van" },
  { code: "reefer", label: "Reefer" },
  { code: "flatbed", label: "Flatbed" },
  { code: "step_deck", label: "Step Deck" },
  { code: "power_only", label: "Power Only" },
  { code: "hotshot", label: "Hotshot" },
  { code: "multi", label: "Multi (any of the above)" },
];

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

/**
 * CareersEditPage — token-authed edit form for an existing /jobs/:slug page.
 * Route: /careers-edit
 * Query params (optional deep-link): ?slug=xxx&token=yyy → auto-loads.
 * Otherwise renders a slug + token challenge form first.
 */
export default function CareersEditPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();

  const [slugInput, setSlugInput] = useState(params.get("slug") || "");
  const [tokenInput, setTokenInput] = useState(params.get("token") || "");
  const [loadingLoad, setLoadingLoad] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [doc, setDoc] = useState(null); // full editable doc + counts

  // Auto-load if both query params are present
  useEffect(() => {
    if (params.get("slug") && params.get("token") && !doc && !loadingLoad) {
      loadPage(params.get("slug"), params.get("token"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPage = async (slug, token) => {
    setLoadError("");
    setLoadingLoad(true);
    try {
      const { data } = await axios.get(
        `${API_BASE}/api/careers/pages/${encodeURIComponent(slug)}/edit`,
        { params: { token } },
      );
      setDoc({ ...data, edit_token: token });
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) setLoadError("That edit token doesn't match this careers page. Double-check the token and try again.");
      else if (status === 404) setLoadError("No careers page found with that slug.");
      else setLoadError(err.response?.data?.detail || "Couldn't load the page. Try again.");
    } finally {
      setLoadingLoad(false);
    }
  };

  const submitChallenge = (e) => {
    e.preventDefault();
    if (!slugInput.trim() || !tokenInput.trim()) {
      setLoadError("Both the slug (e.g., yaga-empire) and the edit token are required.");
      return;
    }
    // Sync URL for shareable link (and so a browser refresh reloads state)
    nav(`/careers-edit?slug=${encodeURIComponent(slugInput.trim())}&token=${encodeURIComponent(tokenInput.trim())}`);
    loadPage(slugInput.trim(), tokenInput.trim());
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <TopNav />
      {!doc && (
        <ChallengeForm
          slug={slugInput} setSlug={setSlugInput}
          token={tokenInput} setToken={setTokenInput}
          loading={loadingLoad} error={loadError}
          onSubmit={submitChallenge}
        />
      )}
      {doc && (
        <EditForm
          doc={doc}
          onDone={(nextSlug) => {
            // reload with fresh data if slug still active
            loadPage(nextSlug || doc.slug, doc.edit_token);
          }}
        />
      )}
    </div>
  );
}

/* ─────────────────── Challenge (slug + token) ─────────────────── */
function ChallengeForm({ slug, setSlug, token, setToken, loading, error, onSubmit }) {
  return (
    <>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #FF5A1F 0%, transparent 40%), radial-gradient(circle at 80% 70%, #FF5A1F 0%, transparent 40%)" }} />
        <div className="relative max-w-3xl mx-auto px-6 pt-16 pb-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-4 py-1.5 text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-6">
            <KeyRound className="w-3.5 h-3.5" />
            Edit an existing careers page
          </div>
          <h1 className="text-4xl sm:text-5xl font-black leading-[1.02] tracking-tight mb-3">
            Update your <span className="text-[#FF5A1F]">careers page</span>
          </h1>
          <p className="text-base text-white/70 max-w-xl mx-auto leading-relaxed">
            Paste the URL slug (the part after <code className="text-[#FF5A1F]">/jobs/</code>) and the edit token we gave you when you published the page.
          </p>
        </div>
      </section>

      <section className="max-w-xl mx-auto px-6 pb-16">
        <form onSubmit={onSubmit} className="rounded-2xl border-2 border-white/10 bg-white/5 p-6 sm:p-8 space-y-4" data-testid="careers-edit-challenge-form">
          <div>
            <FieldLabel>Page slug</FieldLabel>
            <div className="flex items-center gap-1.5">
              <span className="text-white/40 text-sm font-mono shrink-0">/jobs/</span>
              <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="yaga-empire" testid="careers-edit-slug" required />
            </div>
          </div>
          <div>
            <FieldLabel>Edit token</FieldLabel>
            <Input value={token} onChange={(e) => setToken(e.target.value)} placeholder="Paste the long random token you saved at publish time" testid="careers-edit-token" required />
            <p className="text-[11px] text-white/40 mt-1.5 leading-snug">
              Lost your token? Email <a href="mailto:contact@freightpilotai.ai" className="text-[#FF5A1F] hover:underline">contact@freightpilotai.ai</a> from the same address you used as the page's contact email — we'll verify and re-issue within 2-3 business days.
            </p>
          </div>

          {error && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200 flex items-start gap-2" data-testid="careers-edit-challenge-error">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          )}

          <button type="submit" disabled={loading}
            className="w-full bg-[#FF5A1F] hover:bg-[#FF7038] disabled:bg-white/10 disabled:text-white/40 text-black font-bold rounded-lg px-6 py-3 text-base transition-colors flex items-center justify-center gap-2"
            data-testid="careers-edit-load-btn">
            {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Loading...</>) : (<>Load my page →</>)}
          </button>

          <div className="text-[11px] text-white/40 text-center leading-snug pt-2 border-t border-white/10">
            Need to build a NEW page instead? <Link to="/careers-builder" className="text-[#FF5A1F] hover:underline">Start here →</Link>
          </div>
        </form>
      </section>
    </>
  );
}

/* ─────────────────── Edit form (post-challenge) ─────────────────── */
function EditForm({ doc, onDone }) {
  const [form, setForm] = useState({
    company_name: doc.company_name || "",
    tagline: doc.tagline || "",
    city: doc.city || "",
    state: doc.state || "",
    freight_type: doc.freight_type || "dry_van",
    pay_range: doc.pay_range || "",
    home_time: doc.home_time || "",
    min_experience_years: doc.min_experience_years ?? 2,
    cdl_class: doc.cdl_class || "A",
    contact_email: doc.contact_email || "",
    contact_phone: doc.contact_phone || "",
  });
  const [benefits, setBenefits] = useState(doc.benefits && doc.benefits.length ? [...doc.benefits] : [""]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");
  const [active, setActive] = useState(!!doc.active);
  const [deactivating, setDeactivating] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const addBenefit = () => setBenefits([...benefits, ""]);
  const setBenefit = (i, v) => setBenefits(benefits.map((b, idx) => (idx === i ? v : b)));
  const removeBenefit = (i) => setBenefits(benefits.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSavedAt(null);
    setSaving(true);
    try {
      const payload = {
        edit_token: doc.edit_token,
        ...form,
        min_experience_years: parseInt(form.min_experience_years, 10) || 0,
        benefits: benefits.filter((b) => b && b.trim()),
      };
      await axios.patch(`${API_BASE}/api/careers/pages/${encodeURIComponent(doc.slug)}`, payload);
      setSavedAt(new Date());
      window.scrollTo({ top: 0, behavior: "smooth" });
      onDone(doc.slug);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) setError("The edit token stopped matching. This shouldn't happen — reload and try again.");
      else setError(err.response?.data?.detail || "Save failed. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    setError("");
    setDeactivating(true);
    try {
      if (active) {
        // Deactivate → confirm first
        if (!window.confirm("Take this page offline? Drivers visiting /jobs/" + doc.slug + " will see a 404. You can turn it back on anytime from this same edit page.")) {
          setDeactivating(false);
          return;
        }
        await axios.post(`${API_BASE}/api/careers/pages/${encodeURIComponent(doc.slug)}/deactivate`, {
          edit_token: doc.edit_token,
        });
        setActive(false);
      } else {
        // Re-activate
        await axios.patch(`${API_BASE}/api/careers/pages/${encodeURIComponent(doc.slug)}`, {
          edit_token: doc.edit_token,
          active: true,
        });
        setActive(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't toggle status. Try again.");
    } finally {
      setDeactivating(false);
    }
  };

  const publicUrl = `${window.location.origin}/jobs/${doc.slug}`;

  return (
    <section className="max-w-3xl mx-auto px-6 py-10">
      {/* Stats + status pill */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-1">
            Editing careers page
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">
            {doc.company_name}
          </h1>
          <a href={publicUrl} target="_blank" rel="noreferrer" className="text-sm text-white/50 hover:text-[#FF5A1F] transition-colors inline-flex items-center gap-1 mt-1" data-testid="careers-edit-public-link">
            {publicUrl.replace(/^https?:\/\//, "")} <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] uppercase tracking-widest font-bold ${active ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" : "bg-red-500/15 text-red-300 border border-red-500/40"}`} data-testid="careers-edit-status-pill">
            {active ? "Live" : "Offline"}
          </div>
        </div>
      </div>

      {/* Metrics strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <MetricCard icon={<Eye className="w-4 h-4" />} label="Page views" value={doc.view_count ?? 0} />
        <MetricCard icon={<Users className="w-4 h-4" />} label="Applications" value={doc.application_count ?? 0} />
        <MetricCard icon={<Briefcase className="w-4 h-4" />} label="Since" value={doc.created_at ? new Date(doc.created_at).toLocaleDateString() : "—"} />
      </div>

      {/* Analytics chart — last 14 days */}
      <AnalyticsChart slug={doc.slug} token={doc.edit_token} />

      {savedAt && (
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-200 flex items-center gap-2 mb-4" data-testid="careers-edit-saved">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          Saved · {savedAt.toLocaleTimeString()}
        </div>
      )}

      {/* Referral kickback banner */}
      <ReferralShareCard slug={doc.slug} companyName={doc.company_name} token={doc.edit_token} />

      <form onSubmit={submit} className="rounded-2xl border-2 border-white/10 bg-white/5 p-6 sm:p-8 space-y-5" data-testid="careers-edit-form">
        {/* Company info */}
        <Section title="Your company">
          <Field label="Company name" value={form.company_name} onChange={update("company_name")} testid="careers-edit-company-name" required />
          <Field label="One-line tagline" value={form.tagline} onChange={update("tagline")} testid="careers-edit-tagline" />
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <FieldLabel>Based in (city)</FieldLabel>
              <Input value={form.city} onChange={update("city")} testid="careers-edit-city" required />
            </div>
            <div>
              <FieldLabel>State</FieldLabel>
              <Select value={form.state} onChange={update("state")} testid="careers-edit-state">
                <option value="">ST</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
            </div>
          </div>
        </Section>

        {/* The job */}
        <Section title="The job">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <FieldLabel>Freight type</FieldLabel>
              <Select value={form.freight_type} onChange={update("freight_type")} testid="careers-edit-freight">
                {FREIGHT_TYPES.map((f) => <option key={f.code} value={f.code}>{f.label}</option>)}
              </Select>
            </div>
            <div>
              <FieldLabel>CDL class required</FieldLabel>
              <Select value={form.cdl_class} onChange={update("cdl_class")} testid="careers-edit-cdl">
                <option value="A">Class A</option>
                <option value="B">Class B</option>
                <option value="A_or_B">A or B</option>
              </Select>
            </div>
          </div>
          <Field label="Pay range" value={form.pay_range} onChange={update("pay_range")} testid="careers-edit-pay" required />
          <Field label="Home time policy" value={form.home_time} onChange={update("home_time")} testid="careers-edit-hometime" required />
          <div>
            <FieldLabel>Min. years of experience</FieldLabel>
            <Input type="number" value={form.min_experience_years} onChange={update("min_experience_years")} min="0" max="30" testid="careers-edit-experience" />
          </div>
        </Section>

        {/* Benefits */}
        <Section title="What makes your fleet different">
          <div className="space-y-2">
            {benefits.map((b, i) => (
              <div key={i} className="flex gap-2">
                <Input value={b} onChange={(e) => setBenefit(i, e.target.value)} placeholder="e.g., Brand new 2026 Freightliner Cascadia" testid={`careers-edit-benefit-${i}`} />
                {benefits.length > 1 && (
                  <button type="button" onClick={() => removeBenefit(i)}
                    className="shrink-0 w-11 rounded-lg border border-white/10 hover:border-red-500/40 hover:bg-red-500/10 text-white/40 hover:text-red-400 transition-colors flex items-center justify-center"
                    data-testid={`careers-edit-benefit-remove-${i}`}>
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {benefits.length < 15 && (
            <button type="button" onClick={addBenefit}
              className="text-sm text-[#FF5A1F] hover:text-[#FF7038] flex items-center gap-1 transition-colors"
              data-testid="careers-edit-benefit-add">
              <Plus className="w-4 h-4" /> Add another
            </button>
          )}
        </Section>

        {/* Contact */}
        <Section title="How drivers reach you">
          <Field label="Contact email (applications forwarded here)" type="email" value={form.contact_email} onChange={update("contact_email")} testid="careers-edit-email" required />
          <Field label="Contact phone (optional, shown publicly)" value={form.contact_phone} onChange={update("contact_phone")} testid="careers-edit-phone" />
        </Section>

        {error && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200 flex items-start gap-2" data-testid="careers-edit-error">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>{error}</div>
          </div>
        )}

        <button type="submit" disabled={saving}
          className="w-full bg-[#FF5A1F] hover:bg-[#FF7038] disabled:bg-white/10 disabled:text-white/40 text-black font-bold rounded-lg px-6 py-3 text-base transition-colors flex items-center justify-center gap-2"
          data-testid="careers-edit-save-btn">
          {saving ? (<><Loader2 className="w-5 h-5 animate-spin" /> Saving...</>) : (<>Save changes</>)}
        </button>
      </form>

      {/* Danger zone — take page offline */}
      <div className="mt-8 rounded-2xl border-2 border-red-500/30 bg-red-500/5 p-5 sm:p-6" data-testid="careers-edit-danger-zone">
        <div className="text-[11px] uppercase tracking-widest text-red-300 font-bold mb-2 flex items-center gap-1.5">
          <AlertTriangle className="w-4 h-4" />
          Danger zone
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <div className="text-white font-semibold text-sm">
              {active ? "Take this page offline" : "This page is currently offline"}
            </div>
            <div className="text-white/50 text-[13px] mt-0.5">
              {active
                ? "Drivers visiting /jobs/" + doc.slug + " will see a 404. Existing applications stay in your inbox. You can turn it back on anytime."
                : "The page is not visible to drivers. Applications are also blocked. Re-activate below when you're hiring again."}
            </div>
          </div>
          <button type="button" onClick={toggleActive} disabled={deactivating}
            className={`shrink-0 inline-flex items-center gap-2 font-bold rounded-lg px-5 py-2.5 text-sm transition-colors ${active
              ? "bg-red-500/20 hover:bg-red-500 hover:text-white text-red-200 border border-red-500/40"
              : "bg-emerald-500 hover:bg-emerald-400 text-black"}`}
            data-testid="careers-edit-toggle-active-btn">
            {deactivating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : active ? (
              <><PowerOff className="w-4 h-4" /> Take offline</>
            ) : (
              <><Power className="w-4 h-4" /> Re-activate</>
            )}
          </button>
        </div>
      </div>

      <div className="text-center mt-6 text-[12px] text-white/40">
        Slug and edit token are permanent — bookmark this URL to come back later.
      </div>
    </section>
  );
}

/* ─────────────────── Small helpers ─────────────────── */
function TopNav() {
  return (
    <div className="border-b border-white/10 bg-neutral-950/80 backdrop-blur sticky top-0 z-30">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-baseline gap-1" data-testid="careers-edit-home-link">
          <span className="font-black text-xl tracking-tight">Freight</span>
          <span className="font-black text-xl tracking-tight text-[#FF5A1F]">Pilot</span>
          <span className="font-black text-xl tracking-tight">AI</span>
        </Link>
        <div className="flex items-center gap-4 text-sm">
          <Link to="/tools" className="text-white/70 hover:text-white transition-colors hidden sm:inline">
            All Tools →
          </Link>
          <Link to="/" className="text-white/70 hover:text-white transition-colors">
            ← Home
          </Link>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-3 flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-[#FF5A1F]/15 text-[#FF5A1F] flex items-center justify-center">
        {icon}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{label}</div>
        <div className="text-lg font-black tracking-tight">{value}</div>
      </div>
    </div>
  );
}

/* ─────────────────── Referral share card ─────────────────── */

const SHARE_TEMPLATES = [
  { id: "orange", label: "Brand", desc: "Dark + orange" },
  { id: "bold-red", label: "Alert Red", desc: "Red · loud" },
  { id: "minimalist", label: "Minimalist", desc: "All black · clean" },
];

function ReferralShareCard({ slug, companyName, token }) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [template, setTemplate] = useState("orange");
  const [insights, setInsights] = useState(null);
  const shareUrl = `${window.location.origin}/jobs/${slug}?ref=${encodeURIComponent(slug)}`;

  const loadInsights = React.useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/careers/pages/${encodeURIComponent(slug)}/share-image/insights?token=${encodeURIComponent(token)}`,
      );
      if (res.ok) setInsights(await res.json());
    } catch { /* silent — pill just doesn't render */ }
  }, [slug, token]);

  React.useEffect(() => { loadInsights(); }, [loadInsights]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // fallthrough
    }
  };
  const downloadShareImage = async () => {
    if (!token) return;
    setDownloading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/careers/pages/${encodeURIComponent(slug)}/share-image.png?token=${encodeURIComponent(token)}&template=${encodeURIComponent(template)}`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `freightpilotai-referral-${slug}-${template}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // Refresh insights so the winner pill updates immediately
      loadInsights();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("share-image download failed:", e);
    } finally {
      setDownloading(false);
    }
  };
  const templateCounts = React.useMemo(() => {
    const m = {};
    (insights?.by_template || []).forEach((r) => { m[r.template] = r.count; });
    return m;
  }, [insights]);
  return (
    <div className="rounded-2xl border-2 border-emerald-500/30 bg-emerald-500/[0.05] p-5 sm:p-6 mb-6" data-testid="careers-edit-referral-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center shrink-0">
          <Gift className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-emerald-300 font-bold mb-1">
            Earn 30 free days
          </div>
          <div className="text-white font-bold text-base mb-1">
            Refer another carrier — both of you get 30 days free.
          </div>
          <div className="text-[13px] text-white/60 leading-snug mb-3">
            Share the link below on trucking Facebook groups, r/Truckers, or your
            TikTok bio. Every carrier who signs up on FreightPilotAI via this
            link earns you a <b className="text-emerald-300">30-day trial extension</b> — and
            they get 30 free days too.
          </div>
          <div className="flex items-center gap-2 rounded-md bg-black/40 border border-white/10 p-2.5">
            <div className="flex-1 font-mono text-[12px] text-white/70 truncate" data-testid="careers-edit-referral-url">
              {shareUrl}
            </div>
            <button type="button" onClick={copy}
              data-testid="careers-edit-referral-copy-btn"
              className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-bold whitespace-nowrap transition-colors ${copied
                ? "bg-emerald-500 text-black"
                : "bg-emerald-500/20 hover:bg-emerald-500 hover:text-black text-emerald-200 border border-emerald-500/40"}`}>
              {copied ? (<><Check className="w-3.5 h-3.5" /> Copied</>) : (<><Copy className="w-3.5 h-3.5" /> Copy link</>)}
            </button>
          </div>

          {/* Template picker + winner insights */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Pick a share-image style</div>
              {insights && insights.winner && insights.total_downloads >= 2 && (
                <div
                  data-testid="careers-edit-share-winner-pill"
                  className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                >
                  🥇 {SHARE_TEMPLATES.find((t) => t.id === insights.winner)?.label || insights.winner} wins for you
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2" data-testid="careers-edit-share-templates">
              {SHARE_TEMPLATES.map((t) => {
                const count = templateCounts[t.id] || 0;
                const isWinner = insights?.winner === t.id && insights.total_downloads >= 2;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTemplate(t.id)}
                    data-testid={`careers-edit-share-template-${t.id}`}
                    aria-pressed={template === t.id}
                    className={`relative rounded-lg border p-2 text-left transition-colors ${template === t.id
                      ? "border-emerald-500/60 bg-emerald-500/[0.08] ring-1 ring-emerald-500/40"
                      : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}
                  >
                    {isWinner && (
                      <div
                        aria-hidden
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-emerald-500 text-black text-[10px] font-black flex items-center justify-center shadow-lg"
                        data-testid={`careers-edit-share-template-${t.id}-medal`}
                      >
                        🥇
                      </div>
                    )}
                    <SharePreviewSwatch id={t.id} />
                    <div className="flex items-center justify-between mt-1.5">
                      <div className={`text-[12px] font-bold ${template === t.id ? "text-emerald-200" : "text-white"}`}>{t.label}</div>
                      {count > 0 && (
                        <div
                          className="text-[10px] font-mono text-white/60 tabular-nums"
                          data-testid={`careers-edit-share-template-${t.id}-count`}
                          title={`${count} download${count === 1 ? "" : "s"}`}
                        >
                          {count}×
                        </div>
                      )}
                    </div>
                    <div className="text-[10px] text-white/50 leading-tight">{t.desc}</div>
                  </button>
                );
              })}
            </div>
            {insights && insights.total_downloads === 0 && (
              <div className="text-[11px] text-white/40 mt-2 italic">
                Download all 3 variants and post them across trucking Facebook groups. We'll tell you which one wins for your audience.
              </div>
            )}
          </div>

          <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
            <button
              type="button"
              onClick={downloadShareImage}
              disabled={!token || downloading}
              data-testid="careers-edit-share-image-btn"
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold whitespace-nowrap transition-colors bg-white/10 hover:bg-emerald-500 hover:text-black text-emerald-200 border border-emerald-500/40 disabled:opacity-50"
            >
              {downloading ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Building image…</>
              ) : (
                <><ImageIcon className="w-3.5 h-3.5" /> Download share image (PNG)</>
              )}
            </button>
            <div className="text-[11px] text-white/50 leading-snug">
              1200×630 branded PNG with your fleet name + QR code — drop it straight into a Facebook trucking group.
            </div>
          </div>
          <div className="text-[11px] text-white/40 mt-2 leading-snug">
            Attribution works even if the visitor lands on your careers page
            first and signs up later — we track the referral via <code className="text-emerald-300">?ref={slug}</code>.
          </div>
        </div>
      </div>
    </div>
  );
}

/* Small preview swatch — miniature bar approximating the template's
   dominant colors. Deliberately cheap (pure CSS) so we don't have to
   render 3 PIL images just for the picker thumbnails. */
function SharePreviewSwatch({ id }) {
  const styles = {
    "orange": { bg: "#0A0A0A", stripe: "#FF5A1F", text: "text-[#FF5A1F]" },
    "bold-red": { bg: "#0E0303", stripe: "#dc2626", text: "text-red-300" },
    "minimalist": { bg: "#050505", stripe: "#ffffff", text: "text-white" },
  };
  const s = styles[id] || styles.orange;
  return (
    <div
      className="w-full aspect-[16/9] rounded overflow-hidden flex"
      style={{ background: s.bg }}
    >
      <div className="w-1 h-full" style={{ background: s.stripe }} />
      <div className="flex-1 flex flex-col justify-center px-2 gap-0.5">
        <div className={`text-[7px] uppercase tracking-widest font-bold ${s.text}`}>Refer &amp; earn</div>
        <div className="h-[3px] w-4/5 rounded" style={{ background: "rgba(255,255,255,0.9)" }} />
        <div className="h-[2px] w-3/5 rounded mt-0.5" style={{ background: "rgba(255,255,255,0.4)" }} />
      </div>
      <div className="w-4 h-full my-auto mr-1 rounded-sm bg-white/95" style={{ aspectRatio: "1/1" }} />
    </div>
  );
}

/* ─────────────────── Analytics chart (last 14 days) ─────────────────── */
function AnalyticsChart({ slug, token }) {
  const [data, setData] = useState(null);
  const [rangeDays, setRangeDays] = useState(14);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_BASE}/api/careers/pages/${encodeURIComponent(slug)}/analytics`, {
      params: { token, days: rangeDays },
    })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, token, rangeDays]);

  return (
    <div className="rounded-2xl border-2 border-white/10 bg-white/[0.03] p-5 sm:p-6 mb-6" data-testid="careers-edit-analytics">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold">
            Traffic + Applications
          </div>
          <div className="text-sm text-white/50 mt-0.5">
            Last {rangeDays} days
          </div>
        </div>
        <div className="flex gap-1 text-[11px]" data-testid="careers-edit-analytics-range">
          {[7, 14, 30].map((d) => (
            <button key={d} type="button" onClick={() => setRangeDays(d)}
              data-testid={`careers-edit-analytics-range-${d}`}
              className={`px-2.5 py-1 rounded font-bold uppercase tracking-wider transition-colors ${rangeDays === d
                ? "bg-[#FF5A1F] text-black"
                : "bg-white/5 hover:bg-white/10 text-white/60"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-white/40 py-10 text-center">Loading analytics…</div>
      ) : !data ? (
        <div className="text-sm text-white/40 py-10 text-center">
          Couldn't load analytics. Try refreshing the page.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 mb-5" data-testid="careers-edit-analytics-totals">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">Views (range)</div>
              <div className="text-2xl font-black tracking-tight text-white" data-testid="careers-edit-analytics-views">
                {data.totals.views_in_range}
              </div>
              <div className="text-[10px] text-white/40">{data.totals.views_all_time} all time</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">Applications (range)</div>
              <div className="text-2xl font-black tracking-tight text-[#FF5A1F]" data-testid="careers-edit-analytics-apps">
                {data.totals.applications_in_range}
              </div>
              <div className="text-[10px] text-white/40">{data.totals.applications_all_time} all time</div>
            </div>
          </div>

          <MiniBars label="Views" series={data.views_by_day.map((d) => ({ label: d.date, value: d.views }))} tone="views" />
          <div className="h-3" />
          <MiniBars label="Applications" series={data.applications_by_day.map((d) => ({ label: d.date, value: d.applications }))} tone="apps" />

          {data.totals.views_in_range === 0 && data.totals.applications_in_range === 0 && (
            <div className="mt-5 text-[12px] text-white/50 leading-relaxed border-t border-white/10 pt-4" data-testid="careers-edit-analytics-empty-hint">
              No traffic yet in this window. Share your page URL in trucking Facebook groups, on your TikTok bio, and on the "Careers" line of your load board profiles. Every application costs you nothing on FreightPilotAI.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MiniBars({ label, series, tone }) {
  const max = Math.max(1, ...series.map((s) => s.value));
  const barColor = tone === "apps" ? "bg-[#FF5A1F]" : "bg-white/60";
  const bgColor = tone === "apps" ? "bg-[#FF5A1F]/10" : "bg-white/[0.06]";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-1.5">
        {label}
      </div>
      <div className="flex items-end gap-[3px] h-14" data-testid={`careers-edit-analytics-bars-${tone}`}>
        {series.map((s, i) => {
          const heightPct = (s.value / max) * 100;
          return (
            <div key={i} className={`flex-1 flex flex-col items-center justify-end ${bgColor} rounded-sm relative group`}>
              <div
                className={`w-full ${barColor} rounded-sm transition-all`}
                style={{ height: `${Math.max(heightPct, s.value > 0 ? 8 : 0)}%` }}
              />
              <div className="hidden group-hover:block absolute -top-8 whitespace-nowrap bg-neutral-950 border border-white/10 rounded px-2 py-1 text-[10px] text-white z-10 pointer-events-none">
                {formatShortDate(s.label)}: {s.value}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[9px] text-white/30 mt-1 font-mono">
        <span>{formatShortDate(series[0]?.label)}</span>
        <span>{formatShortDate(series[series.length - 1]?.label)}</span>
      </div>
    </div>
  );
}

function formatShortDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T12:00:00Z").toLocaleDateString(undefined, {
      month: "short", day: "numeric",
    });
  } catch {
    return iso.slice(5);
  }
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-3 pb-2 border-b border-white/10">
        {title}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function FieldLabel({ children }) {
  return <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1.5">{children}</div>;
}

function Field({ label, value, onChange, placeholder, type = "text", testid, required }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <Input type={type} value={value} onChange={onChange} placeholder={placeholder} testid={testid} required={required} />
    </div>
  );
}

function Input({ type = "text", value, onChange, placeholder, testid, required, min, max }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} required={required} min={min} max={max}
      className="w-full bg-neutral-900 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-lg px-3 py-2.5 text-white placeholder-white/30 outline-none transition-colors"
      data-testid={testid}
    />
  );
}

function Select({ value, onChange, children, testid }) {
  return (
    <select value={value} onChange={onChange}
      className="w-full bg-neutral-900 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-lg px-3 py-2.5 text-white outline-none transition-colors"
      data-testid={testid}>
      {children}
    </select>
  );
}
